import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Trash2,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  ArrowUp,
  ArrowDown,
  Truck,
  Headphones,
  RotateCcw,
  ShieldCheck,
  CreditCard,
  Award,
  Clock,
  Package,
  type LucideIcon,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api'

type Benefit = {
  id: string
  icon: string
  title: string
  subtitle: string
  sortOrder: number
  isActive: boolean
}

const ICONS: Record<string, LucideIcon> = {
  Truck,
  Headphones,
  RotateCcw,
  ShieldCheck,
  CreditCard,
  Award,
  Clock,
  Package,
}

const ICON_NAMES = Object.keys(ICONS)

export function Beneficios() {
  const qc = useQueryClient()
  const [flash, setFlash] = useState<{ tipo: 'ok' | 'err'; msg: string } | null>(null)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState({ icon: 'Truck', title: '', subtitle: '' })

  const { data, isLoading } = useQuery({
    queryKey: ['benefits'],
    queryFn: () => apiGet<{ data: Benefit[] }>('/api/admin/benefits'),
  })

  const items = data?.data ?? []

  function notify(tipo: 'ok' | 'err', msg: string) {
    setFlash({ tipo, msg })
    setTimeout(() => setFlash(null), 3000)
  }

  function refresh() {
    qc.invalidateQueries({ queryKey: ['benefits'] })
  }

  const createMut = useMutation({
    mutationFn: (b: typeof draft & { sort_order: number }) =>
      apiPost<Benefit>('/api/admin/benefits', b),
    onSuccess: () => {
      notify('ok', 'Beneficio creado')
      setAdding(false)
      setDraft({ icon: 'Truck', title: '', subtitle: '' })
      refresh()
    },
    onError: (e: Error) => notify('err', e.message),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Partial<Benefit>) => {
      const payload: Record<string, unknown> = {}
      if (body.icon !== undefined) payload.icon = body.icon
      if (body.title !== undefined) payload.title = body.title
      if (body.subtitle !== undefined) payload.subtitle = body.subtitle
      if (body.sortOrder !== undefined) payload.sort_order = body.sortOrder
      if (body.isActive !== undefined) payload.is_active = body.isActive
      return apiPut<Benefit>(`/api/admin/benefits/${id}`, payload)
    },
    onSuccess: () => refresh(),
    onError: (e: Error) => notify('err', e.message),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiDelete<{ ok: true }>(`/api/admin/benefits/${id}`),
    onSuccess: () => {
      notify('ok', 'Beneficio eliminado')
      refresh()
    },
    onError: (e: Error) => notify('err', e.message),
  })

  function move(item: Benefit, dir: -1 | 1) {
    const idx = items.findIndex((i) => i.id === item.id)
    const swap = items[idx + dir]
    if (!swap) return
    updateMut.mutate({ id: item.id, sortOrder: swap.sortOrder })
    updateMut.mutate({ id: swap.id, sortOrder: item.sortOrder })
  }

  function confirmAdd() {
    if (!draft.title.trim()) {
      notify('err', 'Falta el título')
      return
    }
    const nextOrder = (items.at(-1)?.sortOrder ?? 0) + 1
    createMut.mutate({ ...draft, sort_order: nextOrder })
  }

  return (
    <>
      <Topbar
        title="Beneficios"
        subtitle="Tarjetas reutilizables que se ven en home, checkout y footer (envío, soporte, devoluciones, pagos)"
        actions={
          <>
            {data && <Badge variant="info">{items.length} beneficios</Badge>}
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
            >
              <Plus className="size-3.5" />
              Nuevo beneficio
            </button>
          </>
        }
      />

      {flash && (
        <div
          className={`mx-6 mt-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            flash.tipo === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {flash.tipo === 'ok' ? (
            <CheckCircle2 className="size-4" />
          ) : (
            <AlertCircle className="size-4" />
          )}
          <span>{flash.msg}</span>
        </div>
      )}

      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-ink-500">
            <Loader2 className="size-4 animate-spin" /> Cargando beneficios…
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item, idx) => {
              const Icon = ICONS[item.icon] ?? Award
              return (
                <div
                  key={item.id}
                  className="flex items-center gap-3 rounded-2xl border border-ink-200 bg-white p-3"
                >
                  <select
                    value={item.icon}
                    onChange={(e) =>
                      updateMut.mutate({ id: item.id, icon: e.target.value })
                    }
                    className="rounded-md border border-ink-200 bg-white px-2 py-1 text-xs"
                  >
                    {ICON_NAMES.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>

                  <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-600">
                    <Icon className="size-5" />
                  </div>

                  <div className="grid flex-1 grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={item.title}
                      placeholder="Título"
                      onChange={(e) =>
                        updateMut.mutate({ id: item.id, title: e.target.value })
                      }
                      className="rounded-md border border-transparent bg-transparent px-2 py-1 text-sm font-semibold outline-none focus:border-ink-200 focus:bg-white"
                    />
                    <input
                      type="text"
                      value={item.subtitle}
                      placeholder="Subtítulo"
                      onChange={(e) =>
                        updateMut.mutate({ id: item.id, subtitle: e.target.value })
                      }
                      className="rounded-md border border-transparent bg-transparent px-2 py-1 text-sm outline-none focus:border-ink-200 focus:bg-white"
                    />
                  </div>

                  <button
                    type="button"
                    title={item.isActive ? 'Ocultar' : 'Mostrar'}
                    onClick={() =>
                      updateMut.mutate({ id: item.id, isActive: !item.isActive })
                    }
                    className="grid size-8 place-items-center rounded text-ink-500 hover:bg-ink-100"
                  >
                    {item.isActive ? (
                      <Eye className="size-4 text-emerald-600" />
                    ) : (
                      <EyeOff className="size-4 text-ink-400" />
                    )}
                  </button>

                  <div className="flex items-center">
                    <button
                      type="button"
                      disabled={idx === 0}
                      onClick={() => move(item, -1)}
                      className="grid size-8 place-items-center rounded text-ink-500 hover:bg-ink-100 disabled:opacity-30"
                    >
                      <ArrowUp className="size-4" />
                    </button>
                    <button
                      type="button"
                      disabled={idx === items.length - 1}
                      onClick={() => move(item, 1)}
                      className="grid size-8 place-items-center rounded text-ink-500 hover:bg-ink-100 disabled:opacity-30"
                    >
                      <ArrowDown className="size-4" />
                    </button>
                  </div>

                  <button
                    type="button"
                    title="Eliminar"
                    onClick={() => {
                      if (confirm(`Eliminar "${item.title}"?`)) deleteMut.mutate(item.id)
                    }}
                    className="grid size-8 place-items-center rounded text-red-500 hover:bg-red-50"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
              )
            })}

            {adding && (
              <div className="flex items-center gap-3 rounded-2xl border border-dashed border-brand-300 bg-brand-50 p-3">
                <select
                  value={draft.icon}
                  onChange={(e) => setDraft((d) => ({ ...d, icon: e.target.value }))}
                  className="rounded-md border border-ink-200 bg-white px-2 py-1 text-xs"
                >
                  {ICON_NAMES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>

                <div className="grid flex-1 grid-cols-2 gap-2">
                  <input
                    type="text"
                    autoFocus
                    value={draft.title}
                    placeholder="Título"
                    onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                    className="rounded-md border border-ink-200 bg-white px-2 py-1 text-sm font-semibold outline-none focus:border-brand-500"
                  />
                  <input
                    type="text"
                    value={draft.subtitle}
                    placeholder="Subtítulo"
                    onChange={(e) => setDraft((d) => ({ ...d, subtitle: e.target.value }))}
                    className="rounded-md border border-ink-200 bg-white px-2 py-1 text-sm outline-none focus:border-brand-500"
                  />
                </div>

                <button
                  type="button"
                  onClick={confirmAdd}
                  className="rounded-md bg-brand-600 px-3 py-1 text-xs font-semibold text-white hover:bg-brand-700"
                >
                  Crear
                </button>
                <button
                  type="button"
                  onClick={() => setAdding(false)}
                  className="rounded-md border border-ink-200 bg-white px-3 py-1 text-xs text-ink-600 hover:bg-ink-100"
                >
                  Cancelar
                </button>
              </div>
            )}

            {!isLoading && items.length === 0 && !adding && (
              <div className="rounded-2xl border border-dashed border-ink-200 bg-white p-10 text-center">
                <h3 className="text-sm font-semibold text-ink-900">Sin beneficios</h3>
                <p className="mt-1 text-xs text-ink-500">
                  Crea la primera tarjeta con el botón de arriba.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}
